/**
 * Generato automaticamente.
 * framework-cmp-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  EstrazioneDTO,
  GetBackProcedimentiEstrazioniParams,
  SchedulationResult,
  WsRispostaPaginataListEstrazioneDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * API per ritentare una estrazione in stato FALLITA
 */
export const putBackProcedimentiEstrazioniIdRitenta = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<EstrazioneDTO>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni/${id}/ritenta`,
      method: 'put',
    },
    options,
  );
};

export const getPutBackProcedimentiEstrazioniIdRitentaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackProcedimentiEstrazioniIdRitenta>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putBackProcedimentiEstrazioniIdRitenta>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putBackProcedimentiEstrazioniIdRitenta>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return putBackProcedimentiEstrazioniIdRitenta(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutBackProcedimentiEstrazioniIdRitentaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putBackProcedimentiEstrazioniIdRitenta>>
>;

export type PutBackProcedimentiEstrazioniIdRitentaMutationError =
  ErrorType<unknown>;

export const usePutBackProcedimentiEstrazioniIdRitenta = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putBackProcedimentiEstrazioniIdRitenta>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutBackProcedimentiEstrazioniIdRitentaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Chiamata batch che uno scheduler può effettuare per richiedere il riavvio delle estrazioni
 * @summary Chiamata batch che uno scheduler può effettuare per richiedere il riavvio delle estrazioni
 */
export const postBackProcedimentiEstrazioniRiattiva = (
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni/riattiva`,
      method: 'post',
    },
    options,
  );
};

export const getPostBackProcedimentiEstrazioniRiattivaMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackProcedimentiEstrazioniRiattiva>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postBackProcedimentiEstrazioniRiattiva>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postBackProcedimentiEstrazioniRiattiva>>,
    TVariables
  > = () => {
    return postBackProcedimentiEstrazioniRiattiva(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostBackProcedimentiEstrazioniRiattivaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postBackProcedimentiEstrazioniRiattiva>>
>;

export type PostBackProcedimentiEstrazioniRiattivaMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per richiedere il riavvio delle estrazioni
 */
export const usePostBackProcedimentiEstrazioniRiattiva = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBackProcedimentiEstrazioniRiattiva>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostBackProcedimentiEstrazioniRiattivaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Chiamata batch che uno scheduler può effettuare per eliminare le estrazioni richieste da più di {maxNumDaysForEstrazione} giorni
 * @summary Chiamata batch che uno scheduler può effettuare per eliminare le estrazioni richieste da più di {maxNumDaysForEstrazione} giorni
 */
export const postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione =
  (
    maxNumDaysForEstrazione: number,
    options?: SecondParameter<typeof apiClientRequest>,
  ) => {
    return apiClientRequest<SchedulationResult>(
      {
        url: `/framework-bo-procedimenti/procedimenti/estrazioni/elimina-scadute/${maxNumDaysForEstrazione}`,
        method: 'post',
      },
      options,
    );
  };

export const getPostBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazioneMutationOptions =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione
        >
      >,
      TError,
      { maxNumDaysForEstrazione: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<
        typeof postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione
      >
    >,
    TError,
    { maxNumDaysForEstrazione: number },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } =
      options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione
        >
      >,
      { maxNumDaysForEstrazione: number }
    > = props => {
      const { maxNumDaysForEstrazione } = props ?? {};

      return postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione(
        maxNumDaysForEstrazione,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type PostBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazioneMutationResult =
  NonNullable<
    Awaited<
      ReturnType<
        typeof postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione
      >
    >
  >;

export type PostBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazioneMutationError =
  ErrorType<unknown>;

/**
 * @summary Chiamata batch che uno scheduler può effettuare per eliminare le estrazioni richieste da più di {maxNumDaysForEstrazione} giorni
 */
export const usePostBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione =
  <TError = ErrorType<unknown>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof postBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazione
        >
      >,
      TError,
      { maxNumDaysForEstrazione: number },
      TContext
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  }) => {
    const mutationOptions =
      getPostBackProcedimentiEstrazioniEliminaScaduteMaxNumDaysForEstrazioneMutationOptions(
        options,
      );

    return useMutation(mutationOptions);
  };
/**
 * API per la ricerca paginata delle estrazioni, con la possibilità di filtrare i dati
 */
export const getBackProcedimentiEstrazioni = (
  params?: GetBackProcedimentiEstrazioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListEstrazioneDTO>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetBackProcedimentiEstrazioniQueryKey = (
  params?: GetBackProcedimentiEstrazioniParams,
) =>
  [
    `/framework-bo-procedimenti/procedimenti/estrazioni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetBackProcedimentiEstrazioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackProcedimentiEstrazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackProcedimentiEstrazioniQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>
  > = ({ signal }) =>
    getBackProcedimentiEstrazioni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackProcedimentiEstrazioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>
>;
export type GetBackProcedimentiEstrazioniQueryError = ErrorType<unknown>;

export const useGetBackProcedimentiEstrazioni = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetBackProcedimentiEstrazioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiEstrazioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackProcedimentiEstrazioniQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per la ricerca del dettaglio di una estrazione
 */
export const getBackProcedimentiEstrazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<EstrazioneDTO>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackProcedimentiEstrazioniIdQueryKey = (id: string) =>
  [`/framework-bo-procedimenti/procedimenti/estrazioni/${id}`] as const;

export const getGetBackProcedimentiEstrazioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetBackProcedimentiEstrazioniIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>
  > = ({ signal }) =>
    getBackProcedimentiEstrazioniId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetBackProcedimentiEstrazioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>
>;
export type GetBackProcedimentiEstrazioniIdQueryError = ErrorType<unknown>;

export const useGetBackProcedimentiEstrazioniId = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiEstrazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackProcedimentiEstrazioniIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * API per eliminare una estrazione
 */
export const deleteBackProcedimentiEstrazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<EstrazioneDTO>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteBackProcedimentiEstrazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackProcedimentiEstrazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteBackProcedimentiEstrazioniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteBackProcedimentiEstrazioniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteBackProcedimentiEstrazioniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteBackProcedimentiEstrazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteBackProcedimentiEstrazioniId>>
>;

export type DeleteBackProcedimentiEstrazioniIdMutationError =
  ErrorType<unknown>;

export const useDeleteBackProcedimentiEstrazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteBackProcedimentiEstrazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteBackProcedimentiEstrazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * API per il download del file di una estrazione
 */
export const getBackProcedimentiEstrazioniIdDownload = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni/${id}/download`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackProcedimentiEstrazioniIdDownloadQueryKey = (
  id: string,
) =>
  [
    `/framework-bo-procedimenti/procedimenti/estrazioni/${id}/download`,
  ] as const;

export const getGetBackProcedimentiEstrazioniIdDownloadQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackProcedimentiEstrazioniIdDownloadQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>
  > = ({ signal }) =>
    getBackProcedimentiEstrazioniIdDownload(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetBackProcedimentiEstrazioniIdDownloadQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>
>;
export type GetBackProcedimentiEstrazioniIdDownloadQueryError =
  ErrorType<unknown>;

export const useGetBackProcedimentiEstrazioniIdDownload = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getBackProcedimentiEstrazioniIdDownload>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetBackProcedimentiEstrazioniIdDownloadQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Restituisce il numero di estrazioni in corso sul sistema
 * @summary Restituisce il numero di estrazioni in corso sul sistema
 */
export const getBackProcedimentiEstrazioniNumInCorso = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<number>(
    {
      url: `/framework-bo-procedimenti/procedimenti/estrazioni/num-in-corso`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetBackProcedimentiEstrazioniNumInCorsoQueryKey = () =>
  [`/framework-bo-procedimenti/procedimenti/estrazioni/num-in-corso`] as const;

export const getGetBackProcedimentiEstrazioniNumInCorsoQueryOptions = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetBackProcedimentiEstrazioniNumInCorsoQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>
  > = ({ signal }) =>
    getBackProcedimentiEstrazioniNumInCorso(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetBackProcedimentiEstrazioniNumInCorsoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>
>;
export type GetBackProcedimentiEstrazioniNumInCorsoQueryError =
  ErrorType<unknown>;

/**
 * @summary Restituisce il numero di estrazioni in corso sul sistema
 */
export const useGetBackProcedimentiEstrazioniNumInCorso = <
  TData = Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getBackProcedimentiEstrazioniNumInCorso>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetBackProcedimentiEstrazioniNumInCorsoQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
